import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getAuditLogs,
  getAuditLogTotalCount,
  getUserList,
} from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { auditLogInputData } from '../../shared/constants';
import { Ic } from "../../shared/component/GosIcon";

/* GrowthOS — Audit Log (design: growthos/AuditLog.jsx).
   The design is a friendly day-grouped activity feed over a mock dataset;
   the real audit store (`PublishEventData/Getlist`, redux key `auditLogs`)
   is a technical event log. Each real record is mapped onto a feed item:
     userId          → actor name (resolved via getUserList; else "System")
     operationType   → action kind (create/update/delete/…)  [icon + verb]
     collectionName  → module chip
     createdDateTime → relative time (+ absolute in the meta line)
   Server-side SEARCH (real searchText) + "Load more" (real pageSize paging)
   are preserved; the User/Module/Action filters run client-side over the
   loaded rows (same as the design filters its in-memory events). The design's
   IP column and Export button are dropped — the real data carries no IP and
   there is no audit-export API (nothing fabricated). */

const DEFAULT_SIZE = parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE, 10) || 25;

const KIND = {
  create: { icon: 'plus', tint: '#2c7a45', label: 'Created' },
  update: { icon: 'edit', tint: '#2e63a6', label: 'Updated' },
  delete: { icon: 'trash', tint: '#c0473f', label: 'Deleted' },
  login: { icon: 'user', tint: '#6a4fae', label: 'Signed in' },
  logout: { icon: 'logout', tint: '#6b6353', label: 'Signed out' },
  export: { icon: 'download', tint: '#9a751c', label: 'Exported' },
  perm: { icon: 'link', tint: '#b34480', label: 'Permission' },
  view: { icon: 'eye', tint: '#4f6b8f', label: 'Viewed' },
};



const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#c0473f'];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const colorFor = (name) => (name === 'System' ? '#6b6353' : AVA_COLORS[hashIdx(name, AVA_COLORS.length)]);
const initialsOf = (n) =>
  n === 'System'
    ? 'SY'
    : (n || '?')
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

const minsAgo = (d) => Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
const ago = (m) =>
  m < 1 ? 'just now' : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m / 60)}h ago` : `${Math.floor(m / 1440)}d ago`;
const clockTime = (d) =>
  d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const dayKeyOf = (d) => {
  const now = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(now, d)) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (same(y, d)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

const resolveKind = (row) => {
  const op = (row.operationType || '').toLowerCase();
  const name = `${row.auditableRequestName || ''} ${row.auditableSourceEventName || ''} ${row.apiName || ''}`.toLowerCase();
  if (/login|signin|sign in/.test(name)) return 'login';
  if (/logout|signout|sign out/.test(name)) return 'logout';
  if (/export|download/.test(name)) return 'export';
  if (/permission|grant|role|deactivat/.test(name)) return 'perm';
  if (/insert|create|add|new/.test(op) || /\bcreate|\badd|insert/.test(name)) return 'create';
  if (/delete|remove/.test(op) || /delete|remove/.test(name)) return 'delete';
  if (/read|view|get|fetch/.test(op) && !/update|edit/.test(op)) return 'view';
  if (/update|modify|edit|change|put|patch|upsert/.test(op) || /update|edit/.test(name)) return 'update';
  return 'update';
};

// small dropdown filter (client-side over loaded rows) — ports the design's FilterSelect
const FilterSelect = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);
  return (
    <div className="gos-al-selwrap" ref={ref}>
      <button
        type="button"
        className={`gos-btn gos-btn-ghost gos-al-selbtn${open ? ' on' : ''}${value !== 'All' ? ' has-count' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="gos-al-sel-label">{label}: </span>
        <strong>{value}</strong>
        <Ic name="chevD" size={14} />
      </button>
      {open ? (
        <div className="gos-al-dd">
          {options.map((o) => (
            <button
              key={o}
              type="button"
              className="gos-al-dd-item"
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
            >
              <span className={`gos-al-check${o === value ? ' on' : ''}`}>
                {o === value ? <Ic name="check" size={11} /> : null}
              </span>
              {o}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

function AuditLog({ embedded = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rows = useSelector(getAuditLogs);
  const totalCount = useSelector(getAuditLogTotalCount);
  const userList = useSelector(getUserList);
  const storeLoading = useSelector((state) => isLoadingByKey(state, 'auditLogs'));

  const [search, setSearch] = useState('');
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [actor, setActor] = useState('All');
  const [moduleName, setModuleName] = useState('All');
  const [action, setAction] = useState('All');
  const searchTimer = useRef(null);

  const fetchData = (overrides = {}) => {
    dispatch(
      fetchDataAndUpdateStore({
        key: 'auditLogs',
        inputData: {
          ...auditLogInputData,
          columnName: 'CreatedDateTime',
          orderType: 'desc',
          pageNumber: 1,
          pageSize: size,
          searchText: search.trim() || null,
          ...overrides,
        },
      })
    );
  };

  // initial load + resolve user names
  useEffect(() => {
    fetchData({ pageSize: DEFAULT_SIZE });
    if (!userList || userList.length === 0) dispatch(fetchDataAndUpdateStore({ key: 'userList' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounced server-side search
  const onSearchChange = (val) => {
    setSearch(val);
    setSize(DEFAULT_SIZE);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      dispatch(
        fetchDataAndUpdateStore({
          key: 'auditLogs',
          inputData: {
            ...auditLogInputData,
            columnName: 'CreatedDateTime',
            orderType: 'desc',
            pageNumber: 1,
            pageSize: DEFAULT_SIZE,
            searchText: val.trim() || null,
          },
        })
      );
    }, 400);
  };

  const loadMore = () => {
    const ns = size + DEFAULT_SIZE;
    setSize(ns);
    fetchData({ pageSize: ns });
  };

  const userMap = useMemo(() => {
    const m = {};
    (userList || []).forEach((u) => {
      if (u?.id) m[u.id] = u;
      if (u?.userId) m[u.userId] = u;
    });
    return m;
  }, [userList]);

  const events = useMemo(
    () =>
      (rows || []).map((row, i) => {
        const date = row.createdDateTime ? new Date(row.createdDateTime) : null;
        const u = row.userId ? userMap[row.userId] : null;
        const actorName = u
          ? u.display_name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || row.userId
          : row.userId || 'System';
        const kind = resolveKind(row);
        const mod = row.collectionName || '';
        const desc =
          row.auditableSourceEventName ||
          row.auditableRequestName ||
          row.apiName ||
          `${row.operationType || ''} ${mod}`.trim() ||
          '—';
        return {
          id: row.id || row.correlationId || `ev${i}`,
          actor: actorName,
          kind,
          module: mod,
          desc,
          date,
        };
      }),
    [rows, userMap]
  );

  const actorOpts = useMemo(
    () => ['All', ...Array.from(new Set(events.map((e) => e.actor).filter(Boolean)))],
    [events]
  );
  const moduleOpts = useMemo(
    () => ['All', ...Array.from(new Set(events.map((e) => e.module).filter(Boolean)))],
    [events]
  );
  const actionOpts = useMemo(
    () => ['All', ...Array.from(new Set(events.map((e) => KIND[e.kind].label)))],
    [events]
  );

  const filtered = useMemo(() => {
    let list = events;
    if (actor !== 'All') list = list.filter((e) => e.actor === actor);
    if (moduleName !== 'All') list = list.filter((e) => e.module === moduleName);
    if (action !== 'All') list = list.filter((e) => KIND[e.kind].label === action);
    return list;
  }, [events, actor, moduleName, action]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((e) => {
      const k = e.date ? dayKeyOf(e.date) : 'Earlier';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return [...map.entries()];
  }, [filtered]);

  const activeFilters = (actor !== 'All') + (moduleName !== 'All') + (action !== 'All');
  const clearFilters = () => {
    setActor('All');
    setModuleName('All');
    setAction('All');
  };

  const canLoadMore = (rows?.length || 0) < (totalCount || 0);

  const body = (
      <div className={'gos-auditlog' + (embedded ? ' gos-auditlog-embedded' : '')}>
        <div className="gos-al-head">
          <div>
            {!embedded ? (
              <button type="button" className="gos-al-back" onClick={() => navigate(-1)} title="Back">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              </button>
            ) : null}
            <div className="gos-al-title">Audit Log</div>
            <div className="gos-al-sub">
              A record of every change made across your workspace, newest first.
            </div>
          </div>
        </div>

        <div className="gos-card gos-al-card">
          <div className="gos-al-toolbar">
            <div className="gos-al-search">
              <Ic name="search" size={16} />
              <input
                placeholder="Search activity…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              {search ? (
                <button type="button" className="gos-al-clear" onClick={() => onSearchChange('')}>
                  <Ic name="x" size={14} />
                </button>
              ) : null}
            </div>
            <div className="gos-al-grow" />
            <FilterSelect label="User" value={actor} options={actorOpts} onChange={setActor} />
            <FilterSelect label="Module" value={moduleName} options={moduleOpts} onChange={setModuleName} />
            <FilterSelect label="Action" value={action} options={actionOpts} onChange={setAction} />
            {activeFilters ? (
              <button type="button" className="gos-al-linkbtn" onClick={clearFilters}>
                Clear
              </button>
            ) : null}
          </div>

          <div className="gos-al-scroll">
            {storeLoading && events.length === 0 ? (
              <div className="gos-al-empty">Loading activity…</div>
            ) : groups.length === 0 ? (
              <div className="gos-al-empty">No activity matches your filters.</div>
            ) : (
              groups.map(([day, items]) => (
                <div key={day} className="gos-al-group">
                  <div className="gos-al-day">
                    <span className="gos-al-day-label">{day}</span>
                    <span className="gos-al-day-count">{items.length}</span>
                  </div>
                  <div className="gos-al-timeline">
                    {items.map((e) => {
                      const k = KIND[e.kind];
                      return (
                        <div key={e.id} className="gos-al-item">
                          <span
                            className="gos-al-node"
                            style={{ color: k.tint, background: `${k.tint}1f` }}
                          >
                            <Ic name={k.icon} size={14} />
                          </span>
                          <div className="gos-al-body">
                            <div className="gos-al-line1">
                              <span className="gos-al-ava" style={{ background: colorFor(e.actor) }}>
                                {initialsOf(e.actor)}
                              </span>
                              <span className="gos-al-actor">{e.actor}</span>
                              <span className="gos-al-verb">{k.label.toLowerCase()}</span>
                              {e.module ? <span className="gos-al-mod">{e.module}</span> : null}
                            </div>
                            <div className="gos-al-desc">{e.desc}</div>
                          </div>
                          <div className="gos-al-meta">
                            {e.date ? (
                              <>
                                <span className="gos-al-time" title={clockTime(e.date)}>
                                  {ago(minsAgo(e.date))}
                                </span>
                                <span className="gos-al-abs">{clockTime(e.date)}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="gos-al-foot">
            <span className="gos-al-count">
              Showing {rows?.length || 0}
              {totalCount ? ` of ${totalCount.toLocaleString()}` : ''}
            </span>
            {canLoadMore ? (
              <button
                type="button"
                className="gos-btn gos-btn-ghost gos-btn-sm"
                onClick={loadMore}
                disabled={storeLoading}
              >
                {storeLoading ? 'Loading…' : 'Load more'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
  );

  return embedded ? body : <div className="inner-page">{body}</div>;
}

export default AuditLog;
